import {
	createClient,
	type RealtimeChannel,
	type SupabaseClient,
} from "@supabase/supabase-js";
import { PHASE } from "../../common/constants.ts";
import { applyRealTeamInfo } from "../../common/applyRealTeamInfo.ts";
import {
	MP_PROTOCOL_VERSION,
	SUPABASE_PUBLISHABLE_KEY,
	SUPABASE_URL,
	type MpLeague,
	type MpLeagueSettings,
	type MpMember,
	type MpSession,
	type MpTeam,
} from "../../common/multiplayerConfig.ts";
import { realRosters } from "../core/index.ts";
import getRealTeamPlayerData from "../core/league/create/getRealTeamPlayerData.ts";
import { helpers, toUI } from "../util/index.ts";
import { authStorage, kvGet, kvSet } from "./authStorage.ts";

let client: SupabaseClient | undefined;

export const getClient = () => {
	if (!client) {
		client = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
			auth: {
				storage: authStorage,
				persistSession: true,
				autoRefreshToken: true,
				detectSessionInUrl: false,
			},
		});

		// Keep the top nav's account display in sync on every tab. Fires
		// INITIAL_SESSION on startup and SIGNED_IN/SIGNED_OUT afterwards.
		client.auth.onAuthStateChange((event, session) => {
			toUI("updateLocal", [{ mpEmail: session?.user?.email }]);
		});
	}
	return client;
};

const throwIfError = <T>(result: {
	data: T;
	error: { message: string } | null;
}): T => {
	if (result.error) {
		throw new Error(result.error.message);
	}
	return result.data;
};

// ============================================================================
// Auth
// ============================================================================

export const getSession = async (): Promise<MpSession | undefined> => {
	const { data } = await getClient().auth.getSession();
	const user = data.session?.user;
	if (!user) {
		return undefined;
	}
	return {
		userId: user.id,
		email: user.email ?? "",
	};
};

export const signUp = async (email: string, password: string) => {
	const { data, error } = await getClient().auth.signUp({ email, password });
	if (error) {
		throw new Error(error.message);
	}
	if (!data.session) {
		throw new Error(
			'Account created, but sign-in requires email confirmation. Ask the commissioner to disable "Confirm email" in the Supabase dashboard (Authentication > Sign In / Providers), then sign in normally.',
		);
	}
};

export const signIn = async (email: string, password: string) => {
	const { error } = await getClient().auth.signInWithPassword({
		email,
		password,
	});
	if (error) {
		throw new Error(error.message);
	}
};

export const signOut = async () => {
	await unsubscribeLobby();
	const { error } = await getClient().auth.signOut();
	if (error) {
		throw new Error(error.message);
	}
};

// ============================================================================
// Lobby operations
// ============================================================================

// The team list (and each team's market size) depends on the roster mode:
// real leagues use the actual teams that existed in the chosen season.
export const getLobbyTeams = async (settings: MpLeagueSettings) => {
	if (settings.rosters === "real") {
		const info = await realRosters.getLeagueInfo({
			type: "real",
			season: settings.startingSeason!,
			phase: PHASE.PRESEASON,
			randomDebuts: false,
			randomDebutsKeepCurrent: false,
			realDraftRatings: "rookie",
			realStats: "none",
			includePlayers: false,
		});

		// If the user has configured real team branding (Tools > Global
		// Settings), created leagues will use it, so the lobby should match.
		const { realTeamInfo } = await getRealTeamPlayerData(
			{ fileHasPlayers: false, fileHasTeams: true },
			undefined,
		);

		return info.teams.map((t: any, i: number) => {
			if (realTeamInfo) {
				applyRealTeamInfo(t, realTeamInfo, settings.startingSeason!);
			}
			return {
				tid: t.tid ?? i,
				region: t.region,
				name: t.name,
				abbrev: t.abbrev,
				pop: t.pop ?? 1,
			};
		});
	}

	return helpers.getTeamsDefault().map((t) => ({
		tid: t.tid,
		region: t.region,
		name: t.name,
		abbrev: t.abbrev,
		pop: t.pop,
	}));
};

export const createLobby = async ({
	name,
	displayName,
	settings,
}: {
	name: string;
	displayName: string;
	settings: MpLeagueSettings;
}): Promise<{ league_id: string; invite_code: string }> => {
	const teams = await getLobbyTeams(settings);

	return throwIfError(
		await getClient().rpc("create_lobby", {
			p_name: name,
			p_display_name: displayName,
			p_app_version: MP_PROTOCOL_VERSION,
			p_teams: teams,
			p_settings: settings,
		}),
	);
};

export const joinLobby = async ({
	inviteCode,
	displayName,
}: {
	inviteCode: string;
	displayName: string;
}): Promise<{
	status: "joined" | "not_found" | "version_mismatch" | "already_started";
	league_id?: string;
	league_version?: number;
}> => {
	return throwIfError(
		await getClient().rpc("join_lobby", {
			p_invite_code: inviteCode,
			p_display_name: displayName,
			p_app_version: MP_PROTOCOL_VERSION,
		}),
	);
};

export const leaveLobby = async (leagueId: string) => {
	throwIfError(await getClient().rpc("leave_lobby", { p_league_id: leagueId }));
};

export const claimTeam = async ({
	leagueId,
	tid,
	confirmOutOfBand,
}: {
	leagueId: string;
	tid: number;
	confirmOutOfBand?: boolean;
}): Promise<{
	status: "claimed" | "taken" | "out_of_band" | "already_started";
	pop?: number;
	median?: number;
	band_factor?: number;
	out_of_band?: boolean;
}> => {
	return throwIfError(
		await getClient().rpc("claim_team", {
			p_league_id: leagueId,
			p_tid: tid,
			p_confirm_out_of_band: confirmOutOfBand ?? false,
		}),
	);
};

export const myLeagues = async (): Promise<MpLeague[]> => {
	return throwIfError(await getClient().rpc("my_leagues"));
};

export const getLobbyState = async (
	leagueId: string,
): Promise<{
	league: MpLeague;
	members: MpMember[];
	teams: MpTeam[];
}> => {
	const supabase = getClient();
	const [leagueRes, membersRes, teamsRes] = await Promise.all([
		supabase.from("leagues").select().eq("id", leagueId).single(),
		supabase
			.from("league_members")
			.select()
			.eq("league_id", leagueId)
			.order("joined_at"),
		supabase
			.from("league_teams")
			.select()
			.eq("league_id", leagueId)
			.order("tid"),
	]);

	return {
		league: throwIfError(leagueRes) as MpLeague,
		members: throwIfError(membersRes) as MpMember[],
		teams: throwIfError(teamsRes) as MpTeam[],
	};
};

// ============================================================================
// Realtime
// ============================================================================

let channel: RealtimeChannel | undefined;
let subscribedLeagueId: string | undefined;

const notifyUI = () => {
	toUI("realtimeUpdate", [["multiplayer"]]);
};

export const subscribeLobby = async (leagueId: string) => {
	if (subscribedLeagueId === leagueId) {
		return;
	}
	await unsubscribeLobby();

	// With RLS enabled, postgres_changes only delivers rows the subscriber can
	// see, so the realtime connection must carry the user's token. supabase-js
	// usually wires this up itself on sign-in, but be explicit to be safe.
	const { data } = await getClient().auth.getSession();
	if (data.session) {
		await getClient().realtime.setAuth(data.session.access_token);
	}

	channel = getClient()
		.channel(`lobby-${leagueId}`)
		.on(
			"postgres_changes",
			{
				event: "*",
				schema: "public",
				table: "league_members",
				filter: `league_id=eq.${leagueId}`,
			},
			notifyUI,
		)
		.on(
			"postgres_changes",
			{
				event: "*",
				schema: "public",
				table: "leagues",
				filter: `id=eq.${leagueId}`,
			},
			notifyUI,
		)
		.subscribe();
	subscribedLeagueId = leagueId;
};

export const unsubscribeLobby = async () => {
	if (channel) {
		const c = channel;
		channel = undefined;
		subscribedLeagueId = undefined;
		await getClient().removeChannel(c);
	}
};

// ============================================================================
// Local league id <-> Supabase league id mapping
// ============================================================================

export const setLocalLid = async (leagueId: string, lid: number) => {
	await kvSet(`mpLeagueLid:${leagueId}`, String(lid));
	await kvSet(`mpLidLeague:${lid}`, leagueId);
};

export const getLocalLid = async (
	leagueId: string,
): Promise<number | undefined> => {
	const value = await kvGet(`mpLeagueLid:${leagueId}`);
	return value === null ? undefined : Number(value);
};

export const getLeagueIdForLid = async (
	lid: number,
): Promise<string | undefined> => {
	const value = await kvGet(`mpLidLeague:${lid}`);
	return value === null ? undefined : value;
};

// Set while the sim runner has an unpublished simulation in progress; used to
// detect and restore a diverged local league after a crash or demotion
export const simDirtyKey = (leagueId: string) => `mpSimDirty:${leagueId}`;
